import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router';
import { Cabecera } from '../common/components/Cabecera.js';
import { Pie } from '../common/components/Pie.js';
import { InicioPage } from '../features/inicio/pages/InicioPage.js';
import { ConsultaPage } from '../features/consulta/pages/ConsultaPage.js';
import { FuentesPage } from '../features/fuentes/pages/FuentesPage.js';
import { AutoresEditoresPage } from '../features/estaticas/pages/AutoresEditoresPage.js';
import { ComoFuncionaPage } from '../features/estaticas/pages/ComoFuncionaPage.js';
import { FichaPage } from '../features/fuentes/pages/FichaPage.js';
import { TextoPage } from '../features/estaticas/pages/TextoPage.js';
import { AVISO_LEGAL, PRIVACIDAD, QUIEN_ESTA_DETRAS, TERMINOS } from '../features/estaticas/legal.js';
import { IniciarSesionPage } from '../features/cuentas/pages/IniciarSesionPage.js';
import { RetornoAccesoPage } from '../features/cuentas/pages/RetornoAccesoPage.js';
import { MiCuentaPage } from '../features/cuentas/pages/MiCuentaPage.js';
import { MisEstudiosPage } from '../features/estudios/pages/MisEstudiosPage.js';
import { ConsentimientoOAuthPage } from '../features/cuentas/pages/ConsentimientoOAuthPage.js';
import { AvisoReconsentimiento } from '../features/cuentas/AvisoReconsentimiento.js';
import { useSesion } from '../common/hooks/useSesion.js';
import { useAplicarConsentimientosPendientes } from '../features/cuentas/consentimientosPendientes.js';
import type { Idioma } from '../i18n/index.js';

/** ADMIN-01: solo una cuenta la ve, y trae Recharts; que no pese en el bundle de todo el mundo. */
const AdminPage = lazy(() => import('../features/admin/pages/AdminPage.js').then(m => ({ default: m.AdminPage })));

/**
 * Las rutas de Athena (`plan/F4-web.md`).
 *
 * **El idioma no va en la ruta.** Vive en `localStorage` y en el atributo `lang` del `<html>`: con
 * prefijo, la misma respuesta tendría dos URL y compartir una en un idioma la abriría en el otro
 * para quien la recibe.
 *
 * `/preguntar/:id?` lleva el id de la consulta para poder volver a ella. No la publica: el API solo
 * se la devuelve a su propia sesión.
 */
export function Rutas({ idioma, onCambiarIdioma }: { readonly idioma: Idioma; readonly onCambiarIdioma: (idioma: Idioma) => void }) {
  return (
    <Routes>
      <Route element={<Marco idioma={idioma} onCambiarIdioma={onCambiarIdioma} />}>
        <Route path='/' element={<InicioPage idioma={idioma} />} />
        <Route path='/consulta' element={<ConsultaPage modo='auto' idioma={idioma} />} />
        <Route path='/preguntar/:id?' element={<ConsultaPage modo='preguntar' idioma={idioma} />} />
        <Route path='/contrastar/:id?' element={<ConsultaPage modo='contrastar' idioma={idioma} />} />
        <Route path='/comparar/:id?' element={<ConsultaPage modo='comparar' idioma={idioma} />} />
        <Route path='/fuentes' element={<FuentesPage idioma={idioma} />} />
        <Route path='/fuentes/:slug' element={<FichaPage idioma={idioma} />} />
        <Route path='/autores-y-editores' element={<AutoresEditoresPage idioma={idioma} />} />
        <Route path='/como-funciona' element={<ComoFuncionaPage idioma={idioma} />} />
        <Route
          path='/quien-esta-detras'
          element={<TextoPage titulo={idioma === 'es' ? 'Quién está detrás' : 'Who is behind this'} secciones={QUIEN_ESTA_DETRAS[idioma]} />}
        />
        <Route path='/privacidad' element={<TextoPage titulo={idioma === 'es' ? 'Privacidad' : 'Privacy'} secciones={PRIVACIDAD[idioma]} />} />
        <Route
          path='/aviso-legal'
          element={<TextoPage titulo={idioma === 'es' ? 'Aviso legal' : 'Legal notice'} secciones={AVISO_LEGAL[idioma]} />}
        />
        <Route
          path='/terminos'
          element={<TextoPage titulo={idioma === 'es' ? 'Términos y condiciones' : 'Terms and conditions'} secciones={TERMINOS[idioma]} />}
        />
        <Route path='/iniciar-sesion' element={<IniciarSesionPage idioma={idioma} />} />
        <Route path='/registro' element={<IniciarSesionPage idioma={idioma} registro />} />
        <Route path='/auth/retorno' element={<RetornoAccesoPage idioma={idioma} />} />
        <Route path='/mi-cuenta' element={<MiCuentaPage idioma={idioma} />} />
        <Route path='/mis-estudios' element={<MisEstudiosPage idioma={idioma} />} />
        {/* MCP-01: la pantalla de consentimiento del OAuth Server de Supabase (ruta configurada allí). */}
        <Route path='/oauth/consent' element={<ConsentimientoOAuthPage idioma={idioma} />} />
        <Route
          path='/admin'
          element={
            <Suspense fallback={null}>
              <AdminPage idioma={idioma} />
            </Suspense>
          }
        />
        <Route path='*' element={<Navigate to='/' replace />} />
      </Route>
    </Routes>
  );
}

/** La cabecera y el pie, una sola vez para todas las pantallas. */
function Marco({ idioma, onCambiarIdioma }: { readonly idioma: Idioma; readonly onCambiarIdioma: (idioma: Idioma) => void }) {
  // CUENTAS-01: en cuanto vuelve del enlace mágico con sesión, manda los consentimientos que quedaron
  // pendientes de guardar en /registro (entonces no había usuario todavía).
  const consentimientosListos = useAplicarConsentimientosPendientes(useSesion());
  return (
    <div className='flex min-h-screen flex-col'>
      <Cabecera idioma={idioma} onCambiarIdioma={onCambiarIdioma} />
      {consentimientosListos ? <AvisoReconsentimiento idioma={idioma} /> : null}
      {consentimientosListos ? <Outlet /> : <main className='grow p-6 text-center'>{idioma === 'es' ? 'Cargando…' : 'Loading…'}</main>}
      <Pie idioma={idioma} />
    </div>
  );
}
